import { Tag } from 'antd';
import {
	Genre,
	ProductStatus,
	AgeRating,
	MovementIntensity,
	Language,
	NumberOfPlayers,
	ProductGalleryItemDto,
	GalleryItemType,
	GalleryItemGroup,
	MovementMode,
	CollaboratorPermission,
	GameStatFormat,
	UnitOfMeasurement,
	LeaderboardSortOrder,
	LeaderboardResetFrequency,
	StatementStatus,
	LeaderboardsSortOrder,
	LeaderboardScoreType,
	DownloadableContentAsset,
	DLCAssetsType,
} from 'api';
import { TFunction } from 'i18next';
import { sortBy } from 'lodash';

export const TStatementStatus = {
	[StatementStatus.InProcess]: 'financePage.PaymentsTab.inProcess',
	[StatementStatus.Paid]: 'financePage.PaymentsTab.paid',
	[StatementStatus.Accrued]: 'financePage.PaymentsTab.accrued',
};

export const getGalleryItems = (
	gallery: ProductGalleryItemDto[],
	type: GalleryItemType,
	group: GalleryItemGroup,
) => {
	if (!gallery) return [];

	return gallery.filter((item) => item.type === type && item.group === group);
};

export const getDlcAssettems = (
	gallery: DownloadableContentAsset[],
	type: DLCAssetsType,
) => {
	if (!gallery) return [];

	return gallery.filter((item) => item.type === type);
};

export const getStatuses = (t: TFunction) => [
	{
		value: ProductStatus.Draft,
		name: t('ProductStatus.Draft'),
	},
	{
		value: ProductStatus.Released,
		name: t('ProductStatus.Released'),
	},
];

export const getStatusTag = (
	t: TFunction,
	status?: ProductStatus,
	className?: string,
) => {
	switch (status) {
		case ProductStatus.Draft:
			return (
				<Tag className={className} color="orange">
					{t('ProductStatus.Draft')}
				</Tag>
			);
		case ProductStatus.Released:
			return (
				<Tag className={className} color="green">
					{t('ProductStatus.Released')}
				</Tag>
			);
		case ProductStatus.SubmittedForReview:
			return (
				<Tag className={className} color="cyan">
					{t('ProductStatus.SubmittedForReview')}
				</Tag>
			);
		default:
			return '';
	}
};

export const TProductStatus = {
	[ProductStatus.Draft]: 'ProductStatus.Draft',
	[ProductStatus.SubmittedForReview]: 'ProductStatus.SubmittedForReview',
	[ProductStatus.Released]: 'ProductStatus.Released',
	[ProductStatus.Rejected]: 'ProductStatus.Rejected',
	[ProductStatus.AwaitingChanges]: 'ProductStatus.AwaitingChanges',
	[ProductStatus.ApprovedForStore]: 'ProductStatus.ApprovedForStore',
	[ProductStatus.ApprovedForWorkshop]: 'ProductStatus.ApprovedForWorkshop',
	[ProductStatus.InReview]: 'ProductStatus.InReview',
	[ProductStatus.ChangesSubmitted]: 'ProductStatus.ChangesSubmitted',
};

const TGenre = {
	[Genre.ActionAndAdventure]: 'Genre.ActionAndAdventure',
	[Genre.CardAndBoard]: 'Genre.CardAndBoard',
	[Genre.Casino]: 'Genre.Casino',
	[Genre.Classics]: 'Genre.Classics',
	[Genre.Educational]: 'Genre.Educational',
	[Genre.FamilyAndKids]: 'Genre.FamilyAndKids',
	[Genre.Fighting]: 'Genre.Fighting',
	[Genre.Horror]: 'Genre.Horror',
	[Genre.MultiplayerOnlineBattleArena]: 'Genre.MultiplayerOnlineBattleArena',
	[Genre.Music]: 'Genre.Music',
	[Genre.Other]: 'Genre.Other',
	[Genre.Platformer]: 'Genre.Platformer',
	[Genre.PuzzleAndTrivia]: 'Genre.PuzzleAndTrivia',
	[Genre.RacingAndFlying]: 'Genre.RacingAndFlying',
	[Genre.RolePlaying]: 'Genre.RolePlaying',
	[Genre.Shooter]: 'Genre.Shooter',
	[Genre.Simulation]: 'Genre.Simulation',
	[Genre.Social]: 'Genre.Social',
	[Genre.Sports]: 'Genre.Sports',
	[Genre.Strategy]: 'Genre.Strategy',
};

export const getGenres = (t: TFunction, genres: Genre[]) => {
	return sortBy(
		genres.map((genre) => ({
			value: genre,
			name: t(TGenre[genre]),
		})),
		'name',
	);
};

export const getAllGenres = (t: TFunction) =>
	sortBy(
		getGenres(t, [
			Genre.ActionAndAdventure,
			Genre.CardAndBoard,
			Genre.Casino,
			Genre.Classics,
			Genre.Educational,
			Genre.FamilyAndKids,
			Genre.Fighting,
			Genre.Horror,
			Genre.MultiplayerOnlineBattleArena,
			Genre.Music,
			Genre.Other,
			Genre.Platformer,
			Genre.PuzzleAndTrivia,
			Genre.RacingAndFlying,
			Genre.RolePlaying,
			Genre.Shooter,
			Genre.Simulation,
			Genre.Social,
			Genre.Sports,
			Genre.Strategy,
		]),
		'name',
	);

const TAgeRating = {
	[AgeRating.Everyone]: 'AgeRating.Everyone',
	[AgeRating.Everyone10Plus]: 'AgeRating.Everyone10Plus',
	[AgeRating.Teen]: 'AgeRating.Teen',
	[AgeRating.Mature17Plus]: 'AgeRating.Mature17Plus',
};

export const getAgeRatings = (t: TFunction, ageRatings: AgeRating[]) => {
	return ageRatings.map((rating) => ({
		value: rating,
		name: t(TAgeRating[rating]),
	}));
};

export const getAllAgeRatings = (t: TFunction) =>
	getAgeRatings(t, [
		AgeRating.Everyone,
		AgeRating.Everyone10Plus,
		AgeRating.Teen,
		AgeRating.Mature17Plus,
	]);

const TMovementIntensity = {
	[MovementIntensity.Low]: 'MovementIntensity.Low',
	[MovementIntensity.Moderate]: 'MovementIntensity.Moderate',
	[MovementIntensity.Intense]: 'MovementIntensity.Intense',
};

export const getMovementIntensities = (
	t: TFunction,
	movementIntensities: MovementIntensity[],
) => {
	return movementIntensities.map((movementIntensity) => ({
		value: movementIntensity,
		name: t(TMovementIntensity[movementIntensity]),
	}));
};

export const getAllMovementIntensities = (t: TFunction) =>
	getMovementIntensities(t, [
		MovementIntensity.Low,
		MovementIntensity.Moderate,
		MovementIntensity.Intense,
	]);

const TLanguage = {
	[Language.English]: 'Language.English',
};

export const getLanguages = (t: TFunction, languages: Language[]) => {
	return languages.map((language) => ({
		value: language,
		name: t(TLanguage[language]),
	}));
};

export const getAllLanguages = (t: TFunction) =>
	getLanguages(t, [Language.English]);

const TNumberOfPlayers = {
	[NumberOfPlayers.SinglePlayer]: 'NumberOfPlayers.SinglePlayer',
	[NumberOfPlayers.OnlineMultiplayer]: 'NumberOfPlayers.OnlineMultiplayer',
	[NumberOfPlayers.OnlineCoop]: 'NumberOfPlayers.OnlineCoop',
};

export const getNumberOfPlayers = (
	t: TFunction,
	numberOfPlayers: NumberOfPlayers[],
) => {
	return numberOfPlayers.map((item) => ({
		value: item,
		name: t(TNumberOfPlayers[item]),
	}));
};

export const getAllNumberOfPlayers = (t: TFunction) =>
	getNumberOfPlayers(t, [
		NumberOfPlayers.SinglePlayer,
		NumberOfPlayers.OnlineMultiplayer,
		NumberOfPlayers.OnlineCoop,
	]);
const TMovementModes = {
	[MovementMode.StandingOnly]: 'MovementModes.Standingonly',
	[MovementMode.RoomScale]: 'MovementModes.Roomscale',
	[MovementMode.OmniMovements]: 'MovementModes.OmniMovements',
};
export const getMovementModes = (
	t: TFunction,
	movementModes: MovementMode[],
) => {
	return movementModes.map((item) => ({
		value: item,
		name: t(TMovementModes[item]),
	}));
};

const TCollaboratorPermission = {
	[CollaboratorPermission.Collaborators]: 'Collaborators.Collaborators',
	[CollaboratorPermission.StoreDetails]: 'Collaborators.StoreDetails',
	[CollaboratorPermission.Builds]: 'Collaborators.Builds',
	[CollaboratorPermission.GameServices]: 'Collaborators.GameServices',
	[CollaboratorPermission.Finances]: 'Collaborators.Finances',
	[CollaboratorPermission.Players]: 'Collaborators.Players',
	[CollaboratorPermission.Analytics]: 'Collaborators.Analytics',
	[CollaboratorPermission.RedemptionCodes]: 'Collaborators.RedemptionCodes',
};

export const getCollaboratorPermissions = (
	t: TFunction,
	permissions: CollaboratorPermission[],
) => {
	return permissions.map((item) => ({
		value: item,
		name: t(TCollaboratorPermission[item]),
	}));
};

export const getAllCollaboratorPermissions = (t: TFunction) =>
	sortBy(
		getCollaboratorPermissions(t, [
			CollaboratorPermission.Collaborators,
			CollaboratorPermission.StoreDetails,
			CollaboratorPermission.Builds,
			CollaboratorPermission.GameServices,
			CollaboratorPermission.Finances,
			CollaboratorPermission.Players,
			CollaboratorPermission.Analytics,
			CollaboratorPermission.RedemptionCodes,
		]),
		'value',
	);

export const TGameStatFormate = {
	[GameStatFormat.Integer]:
		'createGameStatsPage.format.gameStatFormatEnum.integer',
	[GameStatFormat.Decimal]:
		'createGameStatsPage.format.gameStatFormatEnum.decimal',
	[GameStatFormat.Percentage]:
		'createGameStatsPage.format.gameStatFormatEnum.percentage',
	[GameStatFormat.HourMinutesSeconds]:
		'createGameStatsPage.format.gameStatFormatEnum.hourMinutesSeconds',
	[GameStatFormat.DaysHoursMinutes]:
		'createGameStatsPage.format.gameStatFormatEnum.daysHoursMinutes',
};

export const TGameStatUnitOfMeasurement = {
	[UnitOfMeasurement.Inches]:
		'createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.inches',
	[UnitOfMeasurement.Feet]:
		'createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.feet',
	[UnitOfMeasurement.Miles]:
		'createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.miles',
	[UnitOfMeasurement.Centimeters]:
		'createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.centimeters',
	[UnitOfMeasurement.Kilometers]:
		'createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.kilometers',
	[UnitOfMeasurement.Milliseconds]:
		'createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.milliseconds',
	[UnitOfMeasurement.Seconds]:
		'createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.seconds',
	[UnitOfMeasurement.Minutes]:
		'createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.minutes',
	[UnitOfMeasurement.Hours]:
		'createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.hours',
	[UnitOfMeasurement.Days]:
		'createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.days',
	[UnitOfMeasurement.Meters]:
		'createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.meters',
	[UnitOfMeasurement.Numbers]:
		'createGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.numbers',
};

export const TLeaderboardSortOrder = {
	[LeaderboardSortOrder.HighestIsLeader]:
		'leaderboardsPage.leaderboardSortOrder.highestIsLeader',
	[LeaderboardSortOrder.LowestIsLeader]:
		'leaderboardsPage.leaderboardSortOrder.lowestIsLeader',
};

export const TLeaderboardV2SortOrder = {
	[LeaderboardsSortOrder.HIGHER_IS_BETTER]:
		'createLeaderboardPage.sortOrderV2.sortOrderEnum.higherIsBetter',
	[LeaderboardsSortOrder.LOWER_IS_BETTER]:
		'createLeaderboardPage.sortOrderV2.sortOrderEnum.lowerIsBetter',
};

export const TLeaderboardV2ScoreType = {
	[LeaderboardScoreType.POINT]:
		'createLeaderboardPage.scoreType.scoreTypeEnum.point',
	[LeaderboardScoreType.TIME_IN_MILLISECOND]:
		'createLeaderboardPage.scoreType.scoreTypeEnum.timeInMillisecond',
	[LeaderboardScoreType.TIME_IN_SECOND]:
		'createLeaderboardPage.scoreType.scoreTypeEnum.timeInSecond',
	[LeaderboardScoreType.DISTANCE_IN_METERS]:
		'createLeaderboardPage.scoreType.scoreTypeEnum.distanceInMeters',
	[LeaderboardScoreType.DISTANCE_IN_FOOT]:
		'createLeaderboardPage.scoreType.scoreTypeEnum.distanceInFoot',
	[LeaderboardScoreType.PERCENTAGE]:
		'createLeaderboardPage.scoreType.scoreTypeEnum.percentage',
};

export const TLeaderboardResetFrequency = {
	[LeaderboardResetFrequency.Daily]:
		'createLeaderboardPage.resetFrequency.resetFrequencyEnum.daily',
	[LeaderboardResetFrequency.Monthly]:
		'createLeaderboardPage.resetFrequency.resetFrequencyEnum.monthly',
	[LeaderboardResetFrequency.Weekly]:
		'createLeaderboardPage.resetFrequency.resetFrequencyEnum.weekly',
	[LeaderboardResetFrequency.Yearly]:
		'createLeaderboardPage.resetFrequency.resetFrequencyEnum.yearly',
	[LeaderboardResetFrequency.Never]:
		'createLeaderboardPage.resetFrequency.resetFrequencyEnum.never',
};
